import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Calendar, Building2, Files, Plane, Users, MapPin, EditIcon, DollarSign, Target, Navigation } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TimezoneUtils } from '@/lib/utils/timezone';
import { fetchUsersWithProfiles } from '@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile';
import { TravelRequestService, type TravelDocument } from '@/lib/collections/requestsTravels/user/getRequestTravelFetch';
import { countries, ecuadorProvinces } from '../utils/countrys-provinces';
import { fetchCompanies } from '@/lib/collections/organizations/getOrganizationsFetch';

interface ListTravelsProps {
    userId: string;
}


const COUNTRY_MAP = Object.fromEntries(
    countries.map(country => [country.value, country.label])
);

const PROVINCE_MAP = Object.fromEntries(
    ecuadorProvinces.map(province => [province.value, province.label])
);

const ListTravels = ({ userId }: ListTravelsProps) => {
    const [documents, setDocuments] = useState<TravelDocument[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchTrigger, setSearchTrigger] = useState(0); //para la busqueda cuando se abra el enlace en el correo
    const [page, setPage] = useState(1);
    const limit = 10;
    const [companiesMap, setCompaniesMap] = useState<Map<string, string>>(new Map());

    // Cache de usuarios para mostrar nombres
    const [usersCache, setUsersCache] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        loadUsersCache();
    }, []);

    useEffect(() => {
        loadDocuments();
    }, [userId, selectedYear, selectedMonth, page, searchQuery, statusFilter, searchTrigger]);

    // Funciones helper súper simples
    const getCountryLabel = (value: string) => COUNTRY_MAP[value] || value;
    const getProvinceLabel = (value: string) => PROVINCE_MAP[value] || value;

    // La función formatDestination queda igual
    const formatDestination = (travel: TravelDocument) => {
        const items = travel.isInternational
            ? travel.countries
            : travel.provinces;

        if (!items || items.length === 0) {
            return 'Sin destino especificado';
        }

        const labels = items.map(travel.isInternational ? getCountryLabel : getProvinceLabel);
        return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1} más`;
    };

    const loadUsersCache = async () => {
        try {
            const users = await fetchUsersWithProfiles();
            const cache = new Map<string, string>();
            users.forEach(user => {
                cache.set(user.id, user.name);
            });
            setUsersCache(cache);
        } catch (error) {
            console.error('Error loading users cache:', error);
        }
    };


    const getUserName = (userId: string): string => {
        return usersCache.get(userId) || 'Usuario desconocido';
    };

    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({ value: year.toString(), label: year.toString() });
        }
        return options;
    };

    const getMonthOptions = () => {
        return [
            { value: '01', label: 'Enero' },
            { value: '02', label: 'Febrero' },
            { value: '03', label: 'Marzo' },
            { value: '04', label: 'Abril' },
            { value: '05', label: 'Mayo' },
            { value: '06', label: 'Junio' },
            { value: '07', label: 'Julio' },
            { value: '08', label: 'Agosto' },
            { value: '09', label: 'Septiembre' },
            { value: '10', label: 'Octubre' },
            { value: '11', label: 'Noviembre' },
            { value: '12', label: 'Diciembre' }
        ];
    };


    useEffect(() => {
        fetchCompanies().then(data => {
            const map = new Map<string, string>();
            data.forEach(company => map.set(company.$id, company.name || company.commercialName));
            setCompaniesMap(map);
        });
    }, []);

    //helper
    const getCompanyName = (id: string) => companiesMap.get(id) || id;


    const loadDocuments = async () => {
        try {
            setLoading(true);
            setError(null);
            let monthFilter: string | undefined = undefined;

            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const offset = (page - 1) * limit;
            const result = await TravelRequestService.getTravelsByUser(
                userId,
                monthFilter,
                limit,
                offset,
                statusFilter
            );

            // Filtrar por búsqueda si hay query
            let filteredDocs = result.documents;
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                filteredDocs = result.documents.filter((travel: TravelDocument) =>
                    travel.title?.toLowerCase().includes(query) ||
                    travel.tripPurpose?.toLowerCase().includes(query) ||
                    travel.companies?.some((company: string) => company.toLowerCase().includes(query)) ||
                    travel.clients?.some((client: string) => client.toLowerCase().includes(query)) ||
                    travel.provinces?.some((province: string) => province.toLowerCase().includes(query)) ||
                    travel.countries?.some((country: string) => country.toLowerCase().includes(query)) ||
                    travel.address?.toLowerCase().includes(query)
                );
            }

            setDocuments(filteredDocs);
            setTotal(searchQuery.trim() ? filteredDocs.length : result.total);
        } catch {
            setError('No se pudo cargar la información. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(total / limit);

    const getStatusBadge = (status: string) => {
        const variants = {
            pending: {
                color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                label: 'Pendiente'
            },
            approved: {
                color: 'bg-green-100 text-green-800 border-green-200',
                label: 'Aprobado'
            },
            rejected: {
                color: 'bg-red-100 text-red-800 border-red-200',
                label: 'Rechazado'
            }
        };

        const variant = variants[status as keyof typeof variants] || variants.pending;

        return (
            <span className={`${variant.color} border text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap`}>
                {variant.label}
            </span>
        );
    };



    const formatCompaniesPreview = (companies?: string[]) => {
        if (!companies || companies.length === 0) return 'Sin empresas';
        const names = companies.map(companyId => getCompanyName(companyId));
        if (names.length === 1) return names[0];
        return `${names[0]} +${names.length - 1} más`;
    };


    if (loading && documents.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Solicitudes</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Solicitudes de Viaje</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            <Plane className="w-5 h-5" />
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">
                {/* Header con filtros */}
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-3xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                                Solicitudes de Viaje
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {total} {total === 1 ? 'registro encontrado' : 'registros encontrados'}
                            </p>
                        </div>

                        <Link to="/requests/$id/travels/addTravel" params={{ id: userId }}>
                            <Button className='cursor-pointer shadow-sm hover:shadow-md transition-shadow'>
                                <Plane className="mr-2 h-4 w-4" />
                                Nueva Solicitud
                            </Button>
                        </Link>
                    </div>

                    {/* Filtros */}
                    <div className="flex flex-wrap gap-3">
                        <div className="w-full sm:flex-1 sm:max-w-md">
                            <Input
                                type="text"
                                placeholder="Buscar por título, destino, empresa, cliente..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearchTrigger(t => t + 1);
                                    }
                                }}
                                className="w-full"
                            />
                        </div>

                        <Select value={statusFilter} onValueChange={(value) => {
                            setStatusFilter(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="approved">Aprobado</SelectItem>
                                <SelectItem value="rejected">Rechazado</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-32.5">
                                <SelectValue placeholder="Año" />
                            </SelectTrigger>
                            <SelectContent>
                                {/* <SelectItem value="all">Todos los años</SelectItem> */}
                                {getYearOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={selectedMonth} onValueChange={(value) => {
                            setSelectedMonth(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-35">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                {/* <SelectItem value="all">Todos los meses</SelectItem> */}
                                {getMonthOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Lista de documentos */}
                {documents.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Plane className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">No hay solicitudes de viaje</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {(selectedYear !== 'all' || selectedMonth !== 'all')
                                            ? 'Intenta ajustar los filtros de búsqueda'
                                            : 'Comienza creando tu primera solicitud de viaje'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {documents.map((travel) => (
                            <Card
                                key={travel.$id}
                                className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-400  hover:border-l-blue-500  overflow-hidden dark:border-l-blue-500 dark:hover:border-l-blue-600 "
                            >
                                <CardHeader className="bg-linear-to-r from-muted/30 to-transparent pb-3">
                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                                        <div className="space-y-2 flex-1 min-w-0">
                                            <CardTitle className="flex items-start gap-3 text-xl">
                                                <div className="rounded-lg bg-primary/10 p-2 mt-0.5 shrink-0">
                                                    <Navigation className="h-5 w-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                                    <span className="line-clamp-2 leading-tight wrap-break-word">
                                                        {travel.title}
                                                    </span>
                                                    {getStatusBadge(travel.status)}
                                                </div>
                                            </CardTitle>
                                            <CardDescription className="flex items-center gap-2 text-base">
                                                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                                                <span className="font-medium">{formatDestination(travel)}</span>
                                            </CardDescription>
                                        </div>
                                        <div className="flex gap-2 w-full lg:w-auto">
                                            <Dialog>
                                                <DialogTrigger asChild>
                                                    <Button
                                                        size="sm"
                                                        className="flex-1 lg:flex-initial shrink-0 cursor-pointer shadow-sm hover:shadow-md transition-all"
                                                        variant="secondary"
                                                    >
                                                        <Building2 className="mr-2 h-4 w-4" />
                                                        <span>Detalles</span>
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto font-outfit">
                                                    <DialogHeader>
                                                        <DialogTitle className="flex items-center gap-2">
                                                            <Navigation className="h-5 w-5" />
                                                            {travel.title}
                                                        </DialogTitle>
                                                        <DialogDescription>
                                                            {travel.isInternational ? 'Viaje Internacional' : '🇪🇨 Viaje Nacional'}
                                                        </DialogDescription>
                                                    </DialogHeader>

                                                    <div className="overflow-y-auto max-h-[calc(80vh-120px)] pr-2">
                                                        <Tabs defaultValue="general" className="w-full">
                                                            <TabsList className="grid w-full grid-cols-3">
                                                                <TabsTrigger value="general">General</TabsTrigger>
                                                                <TabsTrigger value="detalles">Detalles</TabsTrigger>
                                                                <TabsTrigger value="financiero">Financiero</TabsTrigger>
                                                            </TabsList>

                                                            {/* TAB GENERAL */}
                                                            <TabsContent value="general" className="space-y-4 mt-4">
                                                                <div className="grid gap-4">
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Título:</label>
                                                                        <p className="text-sm font-semibold wrap-break-word " >{travel.title}</p>
                                                                    </div>

                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Motivo:</label>
                                                                        <p className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap text-gray-800 dark:text-gray-400">
                                                                            {travel.tripPurpose}
                                                                        </p>
                                                                    </div>

                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground ">Fecha inicio:</label>
                                                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-400">
                                                                            {TimezoneUtils.formatEcuadorDateOnly(new Date(travel.startDate))}
                                                                            {' a las '}
                                                                            {TimezoneUtils.formatEcuadorTimeOnly(new Date(travel.startDate))}
                                                                        </p>
                                                                    </div>

                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Fecha fin:</label>
                                                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-400">
                                                                            {TimezoneUtils.formatEcuadorDateOnly(new Date(travel.endDate))}
                                                                            {' a las '}
                                                                            {TimezoneUtils.formatEcuadorTimeOnly(new Date(travel.endDate))}
                                                                        </p>
                                                                    </div>

                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Estado:</label>
                                                                        {getStatusBadge(travel.status)}
                                                                    </div>

                                                                    {travel.isGroup && travel.usersId && travel.usersId.length > 1 && (
                                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                            <label className="text-sm font-medium text-muted-foreground">Colaboradores:</label>
                                                                            <div className="space-y-1">
                                                                                {travel.usersId.map((userId, index) => (
                                                                                    <p key={index} className="text-sm text-gray-800 dark:text-gray-400">
                                                                                        • {getUserName(userId)}
                                                                                    </p>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {travel.idApprovedByRrhh && (
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Aprobado por:</label>
                                                                        <p className="text-sm">{getUserName(travel.idApprovedByRrhh)}</p>
                                                                    </div>
                                                                )}

                                                                {travel.idRejectedByRrhh && (
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Rechazado por:</label>
                                                                        <p className="text-sm">{getUserName(travel.idRejectedByRrhh)}</p>
                                                                    </div>
                                                                )}

                                                                {travel.commentByRrhh && (
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Motivo de rechazo:</label>
                                                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                                                                            <p className="text-sm text-red-700 dark:text-red-300 leading-relaxed wrap-break-word whitespace-pre-wrap">
                                                                                {travel.commentByRrhh}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </TabsContent>

                                                            {/* TAB DETALLES */}
                                                            <TabsContent value="detalles" className="space-y-4 mt-4">
                                                                <div className="grid gap-4">
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Destino:</label>
                                                                        <div className="space-y-1">
                                                                            {travel.isInternational ? (
                                                                                travel.countries && travel.countries.length > 0 ? (
                                                                                    travel.countries.map((country, index) => (
                                                                                        <p key={index} className="text-sm text-gray-800 dark:text-gray-400  ">• {getCountryLabel(country)}</p>
                                                                                    ))
                                                                                ) : (
                                                                                    <p className="text-sm text-muted-foreground italic dark:text-gray-400">Sin países especificados</p>
                                                                                )
                                                                            ) : (
                                                                                travel.provinces && travel.provinces.length > 0 ? (
                                                                                    travel.provinces.map((province, index) => (
                                                                                        <p key={index} className="text-sm">• {getProvinceLabel(province)}</p>
                                                                                    ))
                                                                                ) : (
                                                                                    <p className="text-sm text-muted-foreground italic">Sin provincias especificadas</p>
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {travel.address && (
                                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                            <label className="text-sm font-medium text-muted-foreground">Dirección:</label>
                                                                            <p className="text-sm wrap-break-word ">{travel.address}</p>
                                                                        </div>
                                                                    )}

                                                                    {travel.transportationType && (
                                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                            <label className="text-sm font-medium text-muted-foreground">Transporte:</label>
                                                                            <p className="text-sm">
                                                                                {travel.transportationType === 'air' && 'Aéreo'}
                                                                                {travel.transportationType === 'land' && 'Terrestre'}
                                                                                {travel.transportationType === 'sea' && 'Marítimo'}
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                    {travel.companies && travel.companies.length > 0 && (
                                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                            <label className="text-sm font-medium text-muted-foreground">Empresas:</label>
                                                                            <div className="flex flex-wrap gap-2">
                                                                                {travel.companies.map((companyId, index) => (
                                                                                    <div key={index} className="flex items-center gap-1.5 bg-muted/60 border rounded-lg px-3 py-1.5 text-sm">
                                                                                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                                                        {getCompanyName(companyId)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {travel.clients && travel.clients.length > 0 && (
                                                                        <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                            <label className="text-sm font-medium text-muted-foreground">Clientes:</label>
                                                                            <div className="space-y-1">
                                                                                {travel.clients.map((client, index) => (
                                                                                    <p key={index} className="text-sm">• {client}</p>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </TabsContent>

                                                            {/* TAB FINANCIERO */}
                                                            <TabsContent value="financiero" className="space-y-4 mt-4">
                                                                <div className="grid gap-4">
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Costo estimado:</label>
                                                                        <p className="text-sm font-medium">
                                                                            {travel.estimatedCost && travel.estimatedCost.length > 0
                                                                                ? `$${travel.estimatedCost.reduce((total, cost) => total + cost, 0).toFixed(2)} USD`
                                                                                : 'No especificado'}
                                                                        </p>
                                                                    </div>
                                                                    {/* 
                                                                    <div className="grid grid-cols-[120px_1fr] gap-2 items-start">
                                                                        <label className="text-sm font-medium text-muted-foreground">Estado RRHH:</label>
                                                                        {travel.statusByRrhh ? getStatusBadge(travel.statusByRrhh) : (
                                                                            <span className="text-sm text-muted-foreground italic">Pendiente de revisión</span>
                                                                        )}
                                                                    </div> */}



                                                                </div>
                                                            </TabsContent>
                                                        </Tabs>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                            {travel.statusByRrhh === 'approved' ? (
                                                <Button
                                                    size="sm"
                                                    className="flex-1 lg:flex-initial w-full shrink-0"
                                                    variant="outline"
                                                    disabled
                                                >
                                                    <EditIcon className="mr-2 h-4 w-4" />
                                                    <span>Editar</span>
                                                </Button>
                                            ) : (
                                                <Link
                                                    to="/requests/$id/travels/$travelId/updateTravel"
                                                    params={{ id: userId, travelId: travel.$id }}
                                                    className="flex-1 lg:flex-initial"
                                                >
                                                    <Button
                                                        size="sm"
                                                        className="w-full shrink-0 cursor-pointer shadow-sm hover:shadow-md transition-all"
                                                        variant="outline"
                                                    >
                                                        <EditIcon className="mr-2 h-4 w-4" />
                                                        <span>Editar</span>
                                                    </Button>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardContent className="pt-4">
                                    <div className="grid sm:grid-cols-2 gap-6">
                                        {/* Columna izquierda */}
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-3">
                                                <div className="rounded-md bg-blue-500/10 p-2 shrink-0">
                                                    <Target className="h-4 w-4 text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium text-muted-foreground mb-1">Motivo</p>
                                                    <p className="text-sm font-medium wrap-break-word overflow-ellipsis overflow-hidden whitespace-nowrap">
                                                        {travel.tripPurpose.length > 50
                                                            ? `${travel.tripPurpose.slice(0, 50)}...`
                                                            : travel.tripPurpose}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-3">
                                                <div className="rounded-md bg-green-500/10 p-2 shrink-0">
                                                    <Calendar className="h-4 w-4 text-green-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-medium text-muted-foreground mb-1">Fecha de inicio</p>
                                                    <p className="text-sm font-medium">
                                                        {TimezoneUtils.formatEcuadorDateOnly(new Date(travel.startDate))}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-3">
                                                <div className="rounded-md bg-orange-500/10 p-2 shrink-0">
                                                    <Calendar className="h-4 w-4 text-orange-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-medium text-muted-foreground mb-1">Fecha de fin</p>
                                                    <p className="text-sm font-medium">
                                                        {TimezoneUtils.formatEcuadorDateOnly(new Date(travel.endDate))}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Columna derecha */}
                                        <div className="space-y-4">
                                            {travel.isGroup && (
                                                <div className="flex items-start gap-3">
                                                    <div className="rounded-md bg-purple-500/10 p-2 shrink-0">
                                                        <Users className="h-4 w-4 text-purple-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Colaboradores</p>
                                                        <p className="text-sm font-medium">
                                                            {travel.usersId ? travel.usersId.length : 1} persona{travel.usersId && travel.usersId.length > 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {travel.companies && travel.companies.length > 0 && (
                                                <div className="flex items-start gap-3">
                                                    <div className="rounded-md bg-cyan-500/10 p-2 shrink-0">
                                                        <Building2 className="h-4 w-4 text-cyan-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Empresas</p>
                                                        <p className="text-sm font-medium wrap-break-word overflow-ellipsis overflow-hidden whitespace-nowrap">
                                                            {formatCompaniesPreview(travel.companies)}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {travel.estimatedCost && (
                                                <div className="flex items-start gap-3">
                                                    <div className="rounded-md bg-emerald-500/10 p-2 shrink-0">
                                                        <DollarSign className="h-4 w-4 text-emerald-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium text-muted-foreground mb-1">Costo estimado</p>
                                                        <p className="text-sm font-medium">
                                                            ${travel.estimatedCost.reduce((total, cost) => total + cost, 0).toFixed(2)} USD
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-3 pt-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1 || loading}
                            className="shadow-sm"
                        >
                            Anterior
                        </Button>
                        <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-md">
                            <span className="text-sm font-medium">Página {page}</span>
                            <span className="text-muted-foreground">de</span>
                            <span className="text-sm font-medium">{totalPages}</span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages || loading}
                            className="shadow-sm"
                        >
                            Siguiente
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default ListTravels;